import type {ISketchObject} from '../../../../../mesh/sketch/ISketchObject';
import type {SerializablePlainData} from '../../../../../history/domain/SerializablePlainData';
import type {
    SketchCircularPatternConstraintParameters,
    SketchCircularPatternFormulaPayload,
} from '../../../../../../types/sketch/circular-pattern/SketchCircularPatternConstraintTypes';
import {SketchRelationKind} from '../../relation-compatibility/SketchRelationKinds';
import {MultiTargetRelation} from '../MultiTargetRelation';

let patternedConstraintCount = 0;

/**
 * 判断对象是否为圆周阵列聚合关系。
 *
 * @param value 待判断对象。
 * @returns 对象是否为圆周阵列聚合关系。
 */
export function isSketchPatternedConstraint(value: object): value is SketchPatternedConstraint {
    return Reflect.get(value, 'isSketchPatternedConstraint') === true;
}

/**
 * 从持久化或历史快照的公式载荷重建圆周阵列聚合关系。
 *
 * @param payload 未校验的公式载荷。
 * @returns 已完成参数校验的阵列聚合关系。
 * @throws 载荷缺失或字段非法时抛出明确错误。
 */
export function createSketchPatternedConstraintFromFormulaPayload(
    payload: unknown,
): SketchPatternedConstraint {
    return SketchPatternedConstraint.fromFormulaPayload(payload);
}

/**
 * 圆周草图阵列聚合结构关系。
 *
 * 目标顺序固定为“可选阵列中心点、种子目标组、按实例排列的派生目标组”。
 * 一个阵列定义只拥有本类的一个实例；每个目标组可混合点和不同草图实体，
 * 各派生实例所需的刚体变换方程由统一参数编译器在关系内部展开。
 */
export class SketchPatternedConstraint extends MultiTargetRelation<ISketchObject> {
    /** 运行时类型标记。 */
    public readonly isSketchPatternedConstraint = true;

    /** 阵列聚合关系由阵列定义托管，禁止按普通关系删除。 */
    public override readonly isReadOnly = true;

    /** 关系类型。 */
    public relationType = SketchRelationKind.Patterned;

    /** 圆周阵列聚合参数。 */
    private parameters: Required<SketchCircularPatternConstraintParameters>;

    /**
     * 从未知公式载荷创建圆周阵列聚合关系。
     *
     * @param payload 历史或持久化边界读取的未知载荷。
     * @returns 完成字段和实例唯一性校验的聚合关系。
     */
    public static fromFormulaPayload(payload: unknown): SketchPatternedConstraint {
        if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
            throw new Error('圆周阵列聚合关系缺少参数公式载荷。');
        }
        const kind = Reflect.get(payload, 'kind');
        const patternPersistentId = Reflect.get(payload, 'patternPersistentId');
        const rawInstances = Reflect.get(payload, 'instances');
        if (kind !== 'circular-pattern'
            || typeof patternPersistentId !== 'string'
            || !Array.isArray(rawInstances)) {
            throw new Error('圆周阵列聚合关系的参数公式载荷非法。');
        }
        const instances = rawInstances.map((rawInstance) => {
            if (!rawInstance || typeof rawInstance !== 'object' || Array.isArray(rawInstance)) {
                throw new Error('圆周阵列聚合关系包含非法实例参数。');
            }
            const instanceIndex = Reflect.get(rawInstance, 'instanceIndex');
            const rotationRad = Reflect.get(rawInstance, 'rotationRad');
            if (typeof instanceIndex !== 'number' || typeof rotationRad !== 'number') {
                throw new Error('圆周阵列聚合关系包含非法实例参数。');
            }
            return {instanceIndex, rotationRad};
        });
        const rawCenter2d = Reflect.get(payload, 'center2d');
        const rawHasCenterPointReference = Reflect.get(payload, 'hasCenterPointReference');
        const rawSeedTargetKinds = Reflect.get(payload, 'seedTargetKinds');
        const rawRotateInstances = Reflect.get(payload, 'rotateInstances');
        return new SketchPatternedConstraint({
            patternPersistentId,
            instances,
            ...(Array.isArray(rawCenter2d) && rawCenter2d.length === 2
                ? {center2d: [Number(rawCenter2d[0]), Number(rawCenter2d[1])]}
                : {}),
            ...(typeof rawHasCenterPointReference === 'boolean'
                ? {hasCenterPointReference: rawHasCenterPointReference}
                : {}),
            ...(Array.isArray(rawSeedTargetKinds)
                ? {seedTargetKinds: rawSeedTargetKinds as ('entity' | 'point')[]}
                : {}),
            ...(typeof rawRotateInstances === 'boolean'
                ? {rotateInstances: rawRotateInstances}
                : {}),
        });
    }

    /**
     * 创建圆周阵列聚合关系。
     *
     * @param parameters 阵列归属和全部派生实例旋转参数。
     */
    public constructor(parameters: SketchCircularPatternConstraintParameters) {
        super();
        if (!parameters.patternPersistentId) {
            throw new Error('圆周阵列聚合关系缺少阵列持久 ID。');
        }
        if (!Array.isArray(parameters.instances) || parameters.instances.length === 0) {
            throw new Error('圆周阵列聚合关系至少需要一个派生实例。');
        }
        const center2d = parameters.center2d ?? [0, 0];
        const seedTargetKinds = parameters.seedTargetKinds ?? ['entity'];
        if (parameters.hasCenterPointReference === false && parameters.center2d === undefined) {
            throw new Error('圆周阵列聚合关系固定中心缺少坐标。');
        }
        if (center2d.length !== 2 || center2d.some((coordinate) => !Number.isFinite(coordinate))) {
            throw new Error('圆周阵列聚合关系中心坐标非法。');
        }
        if (seedTargetKinds.length === 0
            || seedTargetKinds.some((kind) => kind !== 'entity' && kind !== 'point')) {
            throw new Error('圆周阵列聚合关系种子目标类型非法。');
        }
        const instanceIndexSet = new Set<number>();
        parameters.instances.forEach((instance) => {
            if (!Number.isInteger(instance.instanceIndex) || instance.instanceIndex <= 0) {
                throw new Error('圆周阵列派生实例索引必须是大于 0 的整数。');
            }
            if (instanceIndexSet.has(instance.instanceIndex)) {
                throw new Error(`圆周阵列聚合关系包含重复实例索引：${instance.instanceIndex}。`);
            }
            if (!Number.isFinite(instance.rotationRad)) {
                throw new Error('圆周阵列实例旋转角必须是有限数值。');
            }
            instanceIndexSet.add(instance.instanceIndex);
        });
        this.parameters = {
            patternPersistentId: parameters.patternPersistentId,
            center2d: [center2d[0], center2d[1]],
            hasCenterPointReference: parameters.hasCenterPointReference ?? true,
            seedTargetKinds: [...seedTargetKinds],
            rotateInstances: parameters.rotateInstances ?? true,
            instances: parameters.instances.map((instance) => ({...instance})),
        };
        this.name = `${this.baseDisplayName}${patternedConstraintCount}`;
        patternedConstraintCount += 1;
    }

    /** @returns 关系面板基础名称。 */
    public override get baseDisplayName(): string {
        return '阵列';
    }

    /**
     * 获取阵列聚合参数快照。
     *
     * @returns 与关系内部状态解耦的纯数据快照。
     */
    public getParametersSnapshot(): SketchCircularPatternConstraintParameters {
        return {
            patternPersistentId: this.parameters.patternPersistentId,
            center2d: [this.parameters.center2d[0], this.parameters.center2d[1]],
            hasCenterPointReference: this.parameters.hasCenterPointReference,
            seedTargetKinds: [...this.parameters.seedTargetKinds],
            rotateInstances: this.parameters.rotateInstances,
            instances: this.parameters.instances.map((instance) => ({...instance})),
        };
    }

    /**
     * 把关系归属切换到复制后新建的阵列定义。
     *
     * 几何目标由草图关系克隆链路负责重绑；这里只更新无法由目标对象反推的阵列归属 ID。
     *
     * @param patternPersistentId 复制后的阵列定义持久 ID。
     */
    public remapPatternPersistentId(patternPersistentId: string): void {
        if (!patternPersistentId) {
            throw new Error('圆周阵列聚合关系无法重映射到空阵列持久 ID。');
        }
        this.parameters = {
            ...this.parameters,
            patternPersistentId,
            instances: this.parameters.instances.map((instance) => ({...instance})),
        };
    }

    /**
     * 复制阵列聚合关系的参数定义。
     *
     * @returns 与当前关系参数解耦的新关系实例。
     */
    public override cloneSelf(): SketchPatternedConstraint {
        return new SketchPatternedConstraint(this.parameters);
    }

    /**
     * 创建统一参数编译器需要的纯数据载荷。
     *
     * @returns 圆周阵列聚合载荷。
     */
    public override createParametricFormulaPayload(): SerializablePlainData {
        const payload: SketchCircularPatternFormulaPayload = {
            kind: 'circular-pattern',
            patternPersistentId: this.parameters.patternPersistentId,
            center2d: [this.parameters.center2d[0], this.parameters.center2d[1]],
            hasCenterPointReference: this.parameters.hasCenterPointReference,
            seedTargetKinds: [...this.parameters.seedTargetKinds],
            rotateInstances: this.parameters.rotateInstances,
            instances: this.parameters.instances.map((instance) => ({...instance})),
        };
        return payload as unknown as SerializablePlainData;
    }
}
